import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  connectPageVectorLayout as connectVectorLayoutRequest,
  disconnectPageVectorLayout as disconnectVectorLayoutRequest,
  getVectorLayouts,
} from '../api/layoutApi';

export function useVectorLayoutController({ id, pageData, setPageData, notify }) {
  const [vectorLayouts, setVectorLayouts] = useState([]);
  const [selectedVectorLayoutId, setSelectedVectorLayoutId] = useState('');
  const [hiddenDecorationIds, setHiddenDecorationIds] = useState([]);
  const selectedVectorLayout = useMemo(
    () => vectorLayouts.find((layout) => layout._id === selectedVectorLayoutId) || null,
    [selectedVectorLayoutId, vectorLayouts],
  );

  useEffect(() => {
    const load = async () => {
      if (!pageData?.book_id) return;
      try {
        const response = await getVectorLayouts(pageData.book_id);
        const layouts = response.data.vector_layouts || [];
        setVectorLayouts(layouts);
        setSelectedVectorLayoutId((current) => current && layouts.some((layout) => layout._id === current) ? current : pageData.vector_layout_id || '');
      } catch (error) { console.error('Failed to load vector layouts', error); }
    };
    load();
  }, [pageData?.book_id, pageData?.vector_layout_id]);

  const connectPageVectorLayout = useCallback(async (layout) => {
    if (!id || !layout?._id) return;
    await connectVectorLayoutRequest(id, layout);
    setPageData((previous) => ({ ...(previous || {}), vector_layout_id: layout._id, vector_layout_name: layout.name }));
  }, [id, setPageData]);
  const disconnectPageVectorLayout = useCallback(async () => {
    if (!id) return;
    await disconnectVectorLayoutRequest(id);
    setPageData((previous) => ({ ...(previous || {}), vector_layout_id: '', vector_layout_name: '' }));
    setSelectedVectorLayoutId(''); notify('Saved decoration disconnected from this page.');
  }, [id, notify, setPageData]);
  const handleVectorLayoutSelection = useCallback((layoutId) => { setSelectedVectorLayoutId(layoutId); setHiddenDecorationIds([]); }, []);
  const applyVectorLayout = useCallback(async () => {
    const layout = vectorLayouts.find((item) => item._id === selectedVectorLayoutId);
    if (!layout) { notify('Choose a saved decoration first.', 'error'); return; }
    try { await connectPageVectorLayout(layout); setHiddenDecorationIds([]); notify('Decoration added to this page.'); }
    catch (error) { console.error('Failed to apply graphic overlay', error); notify('Failed to add the decoration', 'error'); }
  }, [connectPageVectorLayout, notify, selectedVectorLayoutId, vectorLayouts]);

  return {
    vectorLayouts, selectedVectorLayout, selectedVectorLayoutId, setSelectedVectorLayoutId,
    hiddenDecorationIds, setHiddenDecorationIds, disconnectPageVectorLayout,
    handleVectorLayoutSelection, applyVectorLayout,
  };
}
